"use client";

import * as React from "react";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/atoms/popover";

import { Button } from "@/components/atoms/button";
import { Calendar } from "@/components/atoms/calendar";
import { DateRange } from "react-day-picker";
import { datePickerFormat, MOBILE_BREAKPOINT } from "@/constants";
import { Icon } from "../icon";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { localeMap } from "@/constants/date";
import { DEFAULT_LOCALE } from "@/i18n/routing";

interface DateRangePickerProps {
  range?: DateRange;
  onChange: (range: DateRange | undefined, selectedDay?: Date) => void;
  onCancel: () => void;
  children?: React.ReactNode;
  locale?: string;
  preventEvent?: boolean;
  onOpenCalendar?: () => void;
  disableDates?: Date[];
  modifierDates?: { [x: string]: Date[] };
  modifierClassnames?: { [x: string]: string };
  noticeComponent?: JSX.Element;
  onMonthChange?: (month: Date) => void;
  isLoading?: boolean;
}

/**
 * DateRangePicker Component
 *
 * A calendar-based popover component that allows users to select a start and end date.
 * - Supports locale-based formatting.
 * - Automatically adjusts for mobile layouts.
 * - Handles edge cases (e.g., selecting the same start and end date).
 */
export function DateRangePicker({
  range,
  onChange,
  onCancel,
  children,
  locale,
  preventEvent,
  onOpenCalendar,
  disableDates,
  modifierDates,
  modifierClassnames,
  noticeComponent,
  isLoading = false,
  onMonthChange,
}: Readonly<DateRangePickerProps>) {
  const [calendarOpen, setCalendarOpen] = React.useState<boolean>(false);
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const currentLocale = locale || DEFAULT_LOCALE;
  const dateLocale = localeMap[currentLocale] || localeMap.en;

  // Closes calendar and confirms the selected range
  const handleApply = () => {
    setCalendarOpen(false);
    onChange(range);
  };

  // Cancels selection and closes the calendar
  const handleCancel = () => {
    setCalendarOpen(false);
    onCancel();
  };

  /**
   * Handles user date selection.
   * Adjusts range if start and end dates are identical (adds +1 day to end date).
   */
  const handleSelect = (
    newRange: DateRange | undefined,
    selectedDay?: Date,
  ) => {
    if (newRange) {
      if (
        newRange.from &&
        newRange.to &&
        newRange.from.getTime() === newRange.to.getTime()
      ) {
        const nextDay = new Date(newRange.to);
        nextDay.setDate(nextDay.getDate() + 1);
        newRange = { ...newRange, to: nextDay };
      }
      onChange(newRange, selectedDay);
    }
  };

  // Formats date for display using locale-specific format
  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return format(date, datePickerFormat, { locale: dateLocale });
  };

  // Locale-specific caption formatter (e.g., Japanese format)
  const customFormatters = React.useMemo(() => {
    if (currentLocale === "ja") {
      return {
        formatCaption: (date: Date) => {
          return format(date, "yyyy年 M月", { locale: dateLocale });
        },
      };
    }
    return undefined;
  }, [currentLocale, dateLocale]);

  // Initialize with today's date if no range provided
  React.useEffect(() => {
    if (!range) {
      onChange({
        from: new Date(),
        to: undefined,
      });
    } else if (range && !range.from) {
      const updatedRange = { ...range, from: new Date() };
      onChange(updatedRange);
    }
  }, [range, onChange]);

  return (
    <div>
      <Popover
        modal
        open={calendarOpen}
        onOpenChange={() => {
          if (preventEvent) {
            return;
          }
          setCalendarOpen((prev) => !prev);
          // Trigger when close
          if (calendarOpen) {
            if (range?.from && !range?.to) {
              const nextDay = new Date(range.from);
              nextDay.setDate(nextDay.getDate() + 1);
              const updatedRange = { ...range, to: nextDay };
              onChange(updatedRange);
            } else {
              onChange(range);
            }
          }
          // Trigger when open
          else if (!calendarOpen) {
            onOpenCalendar?.();
          }
        }}
      >
        <PopoverTrigger asChild>
          {children || (
            <Button
              hierarchy="outline"
              variant="neutral"
              className="text-md gap-3 px-3 py-2"
            >
              {range?.from ? formatDate(range.from) : formatDate(new Date())}
              <Icon icon="arrow-right-small" />
              {range?.to ? formatDate(range.to) : ""}
              <Icon icon="calendar" />
            </Button>
          )}
        </PopoverTrigger>

        {!isLoading && (
          <PopoverContent className="max-h-[440px] w-auto min-w-[284px] overflow-auto rounded-lg border-[var(--stroke-gray-normal)] bg-[var(--fill-base-white)] p-0 shadow-md">
            <Calendar
              mode="range"
              selected={range}
              onSelect={handleSelect}
              defaultMonth={range?.from}
              initialFocus
              numberOfMonths={isMobile ? 1 : 2}
              disabled={[
                { before: new Date() }, // Disable all past dates
                ...(disableDates || []), // Disable specific dates
              ]}
              locale={dateLocale}
              showOutsideDays={false}
              formatters={customFormatters}
              modifiers={modifierDates}
              modifiersClassNames={modifierClassnames}
              onMonthChange={onMonthChange}
            />
            <div
              className={`flex w-full max-w-[calc(100vw-36px)] ${noticeComponent == undefined ? "justify-end" : "justify-between"} flex-row items-center border-t border-[var(--stroke-icon-lighter)] px-2 py-2 sm:gap-2 sm:px-4 md:max-w-screen md:py-4`}
            >
              {noticeComponent}
              <div className="flex justify-end gap-2.5">
                <Button
                  onClick={handleCancel}
                  variant={"primary"}
                  className="px-1 text-[var(--fill-base-main)] sm:px-4"
                  hierarchy={"ghost"}
                >
                  キャンセル
                </Button>
                <Button
                  variant={"primary"}
                  onClick={handleApply}
                  disabled={!range?.from || !range?.to}
                >
                  適用
                </Button>
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
